export const TENANT_CONTENT_STUDIO = "TENANT_CONTENT_STUDIO";

export type FeatureFlag = {
	env: string | undefined;
};

function flags<T extends { [key: string]: FeatureFlag }>(x: T) {
	return x;
}

export const featureFlags = flags({
	// APP-29152: enable only after the gateway schema and upload contract are deployed.
	DOCUMENT_CUSTOM_FIELDS: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_DOCUMENT_CUSTOM_FIELDS,
	},
	TAX_IDENTIFIER_REASONS: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_TAX_IDENTIFIER_REASONS,
	},
	SKIP_ENFORCEMENT_OF_BANK_ACCOUNT_DOCUMENT: {
		env: process.env
			.NEXT_PUBLIC_FEATURE_FLAG_SKIP_ENFORCEMENT_OF_BANK_ACCOUNT_DOCUMENT,
	},
	ENTITY_BANK_ACCOUNT_COUNTRY: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_ENTITY_BANK_ACCOUNT_COUNTRY,
	},
	ALLOCATION_INVESTOR_PREFERENCES: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_ALLOCATION_INVESTOR_PREFERENCES,
	},
	ADMIN_REQUESTED_ACCOUNT_IDV: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_ADMIN_REQUESTED_ACCOUNT_IDV,
	},
	ALLOW_UNAUTHENTICATED_VIEWS: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_ALLOW_UNAUTHENTICATED_VIEWS,
	},
	DISTRIBUTION_REINVESTMENT_PREFERENCE: {
		env: process.env
			.NEXT_PUBLIC_FEATURE_FLAG_DISTRIBUTION_REINVESTMENT_PREFERENCE,
	},
	EDITABLE_INVESTING_ENTITY_DETAILS: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_EDITABLE_INVESTING_ENTITY_DETAILS,
	},
	MFA_REQUIRED: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_MFA_REQUIRED,
	},
	DISABLE_ACCOUNT_CREATION: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_DISABLE_ACCOUNT_CREATION,
	},
	DISABLE_IMPERSONATION_BANNER: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_DISABLE_IMPERSONATION_BANNER,
	},
	DISTRIBUTION_TRANSACTIONS_TABLE: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_DISTRIBUTION_TRANSACTIONS_TABLE,
	},
	FATCA_CRS_COLLECTION: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_FATCA_CRS_COLLECTION,
	},
	// APP-25578: the second pass over FATCA/CRS collection. Always read together
	// with FATCA_CRS_COLLECTION — with only the shipped flag on, the surfaces
	// keep their current behaviour and their current lite fieldset.
	FATCA_CRS_COLLECTION_V2: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_FATCA_CRS_COLLECTION_V2,
	},
	/** Gates APP-25578 application settings on portal (APP-25870 / APP-25875 / APP-25876). */
	FATCA_CRS_APPLICATION_SETTINGS: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_FATCA_CRS_APPLICATION_SETTINGS,
	},
	/** APP-26675: lets a capital raise require all four Nature & Purpose answers on applications (offer.requireNatureAndPurpose). With the raise setting on, the application step shows regardless of FATCA_CRS_COLLECTION and blank answers must be completed. */
	NATURE_AND_PURPOSE_RAISE_SETTING: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_NATURE_AND_PURPOSE_RAISE_SETTING,
	},

	ADVISER_PROJECT_1_SIGNUP_AND_APPLY: {
		env: process.env
			.NEXT_PUBLIC_FEATURE_FLAG_ADVISER_PROJECT_1_SIGNUP_AND_APPLY,
	},
	INVESTMENT_AMOUNT_ALLOCATIONS_CHARTER_HALL: {
		env: process.env
			.NEXT_PUBLIC_FEATURE_FLAG_INVESTMENT_AMOUNT_ALLOCATIONS_CHARTER_HALL,
	},
	// TODO(APP-22669): remove once INVESTMENT_AMOUNT_ALLOCATIONS_CHARTER_HALL is at 100%
	INVESTMENT_AMOUNT_ALLOCATIONS_MVP: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_INVESTMENT_AMOUNT_ALLOCATIONS_MVP,
	},
	SECONDARY_MARKET_TRADE_GROUPED_BY_FUND: {
		env: process.env
			.NEXT_PUBLIC_FEATURE_FLAG_SECONDARY_MARKET_TRADE_GROUPED_BY_FUND,
	},
	INVESTOR_PORTAL_PERMISSIONING: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_INVESTOR_PORTAL_PERMISSIONING,
	},
	ADVISER_PROJECT_2_ADVISER_FEES: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_ADVISER_PROJECT_2_ADVISER_FEES,
	},
	ADVISER_INITIAL_FEE_CAPTURE_DEDUCTION: {
		env: process.env
			.NEXT_PUBLIC_FEATURE_FLAG_ADVISER_INITIAL_FEE_CAPTURE_DEDUCTION,
	},
	XPLAN_DATAFEED_PORTAL: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_XPLAN_DATAFEED_PORTAL,
	},
	DOCUMENTS_STATEMENTS_DISCLAIMER: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_DOCUMENTS_STATEMENTS_DISCLAIMER,
	},
	CHARTER_HALL_VOICE_AGENT: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_CHARTER_HALL_VOICE_AGENT,
	},
	COMMS_PREFERENCES_CATEGORIES: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_COMMS_PREFERENCES_CATEGORIES,
	},
	ACCOUNT_TRANSACTION_CSV: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_ACCOUNT_TRANSACTION_CSV,
	},
	ACCOUNT_DISTRIBUTION_CSV: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_ACCOUNT_DISTRIBUTION_CSV,
	},
	INVESTOR_PORTAL_MAINTENANCE: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_INVESTOR_PORTAL_MAINTENANCE,
	},
	INVESTMENT_DETAILS_STEP: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_INVESTMENT_DETAILS_STEP,
	},
	// Gates the Retail PDS / Wholesale IM investor-type journey (APP-26026): the
	// invest-page declaration/certification gate, per-type document selection and
	// forced download, and the declaration-aware Investment Details step. Must
	// stay off until the jasper-api `investorType` / `primaryDocuments` resolvers
	// land, since those fields currently error.
	WHOLESALE_RETAIL_TYPE: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_WHOLESALE_RETAIL_TYPE,
	},
	// Risk-mitigation toggle (default off): when on, hides the documents feature
	// from the investor portal (nav, holding tab, and /documents route) so it can
	// be pulled from view without taking down the portal. Independent of all
	// other flags.
	HIDE_DOCUMENTS: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_HIDE_DOCUMENTS,
	},
	// Kill switch (default off) for the APP-24253 Add to Investment button on the
	// holding page. When off, the button is hidden regardless of eligibility so it
	// can be pulled from view without a deploy.
	ADD_TO_INVESTMENT: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_ADD_TO_INVESTMENT,
	},
	// Kill switch (default off) for showing an investing entity its applicable minimum; off shows the raise's initial minimum. Also gates admin-app's additional-minimum fields.
	APPLICABLE_OFFER_MINIMUM: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_APPLICABLE_OFFER_MINIMUM,
	},
	MULTI_BATCH_ALLOCATIONS: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_MULTI_BATCH_ALLOCATIONS,
	},
	AUTHENTICATOR_MFA: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_AUTHENTICATOR_MFA,
	},
	AGM_ONLINE_VOTING: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_AGM_ONLINE_VOTING,
	},
	// The admin-app Forms module, which files per-investor PDFs under the
	// Corporate Actions document category. The portal builds no Forms UI of its
	// own; it reads this flag only to decide whether Corporate Actions is worth
	// offering in the document filters. Deliberately the same LaunchDarkly key
	// admin-app uses, so one toggle per tenant governs the whole rollout.
	FORMS_MODULE: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_FORMS_MODULE,
	},
	DOCUMENT_VIEWED_STATE: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_DOCUMENT_VIEWED_STATE,
	},
	[TENANT_CONTENT_STUDIO]: {
		env: process.env.NEXT_PUBLIC_FEATURE_FLAG_TENANT_CONTENT_STUDIO,
	},
});

export type PossibleFeatureFlags = keyof typeof featureFlags;

export const environmentVariableToBoolean = (
	variable: "1" | "0" | "false" | "true" | undefined,
) => {
	switch (variable) {
		case "1":
		case "true":
			return true;
		case "0":
		case "false":
		default:
			return false;
	}
};
